/**
 * What a report shows, kept in the URL: which report, grouped how, for which business and
 * period. In the URL so that opening an order from the report and coming back keeps the filters,
 * and so a report can be bookmarked.
 *
 * The search string is user input, so `parseSearch` keeps only values it recognises, and
 * `resolve` fills in the rest.
 */

export const REPORTS = ["expenses", "income", "orders", "tax", "household"] as const;
export type Report = (typeof REPORTS)[number];

/** The groupings each report accepts, the default first; the API's `group_by`. */
export const GROUPINGS = {
  expenses: ["bucket", "category", "store", "product"],
  income: ["customer", "status", "method"],
  orders: [],
  tax: [],
  household: ["category", "store"],
} as const satisfies Record<Report, readonly string[]>;

export const PERIODS = [
  "this-month",
  "last-month",
  "this-year",
  "last-year",
  "all",
  "custom",
] as const;
export type Period = (typeof PERIODS)[number];

export interface ReportSearch {
  report?: Report;
  by?: string;
  business?: string;
  period?: Period;
  start?: string;
  end?: string;
}

export interface Filters {
  report: Report;
  /** Empty for a report with no groupings. */
  by: string;
  /** Null for every business, and always for the household report. */
  business: string | null;
  period: Period;
  start: string | null;
  end: string | null;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

function oneOf<T extends string>(values: readonly T[], value: unknown): value is T {
  return typeof value === "string" && (values as readonly string[]).includes(value);
}

/** The router's `validateSearch`: what it recognises, and nothing else. */
export function parseSearch(raw: Record<string, unknown>): ReportSearch {
  const search: ReportSearch = {};
  if (oneOf(REPORTS, raw.report)) search.report = raw.report;
  if (typeof raw.by === "string" && raw.by) search.by = raw.by;
  if (typeof raw.business === "string" && raw.business) search.business = raw.business;
  if (oneOf(PERIODS, raw.period)) search.period = raw.period;
  if (typeof raw.start === "string" && ISO_DATE.test(raw.start)) search.start = raw.start;
  if (typeof raw.end === "string" && ISO_DATE.test(raw.end)) search.end = raw.end;
  return search;
}

function pad(value: number): string {
  return String(value).padStart(2, "0");
}

/** The last day of a month, `month` counted from 1. */
function lastDay(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** A preset period's first and last day, from today's date as `YYYY-MM-DD`. */
export function periodRange(
  period: Exclude<Period, "custom">,
  today: string,
): { start: string | null; end: string | null } {
  const year = Number(today.slice(0, 4));
  const month = Number(today.slice(5, 7));
  switch (period) {
    case "this-month":
      return {
        start: `${year}-${pad(month)}-01`,
        end: `${year}-${pad(month)}-${pad(lastDay(year, month))}`,
      };
    case "last-month": {
      const y = month === 1 ? year - 1 : year;
      const m = month === 1 ? 12 : month - 1;
      return { start: `${y}-${pad(m)}-01`, end: `${y}-${pad(m)}-${pad(lastDay(y, m))}` };
    }
    case "this-year":
      return { start: `${year}-01-01`, end: `${year}-12-31` };
    case "last-year":
      return { start: `${year - 1}-01-01`, end: `${year - 1}-12-31` };
    case "all":
      return { start: null, end: null };
  }
}

/** The filters in full: defaults for what the URL leaves out, and dates for a preset. A year
 * is the default period because the reports are mostly read at tax time. */
export function resolve(search: ReportSearch, today: string): Filters {
  const report = search.report ?? "expenses";
  const groupings: readonly string[] = GROUPINGS[report];
  const by = search.by && groupings.includes(search.by) ? search.by : (groupings[0] ?? "");
  const period = search.period ?? "this-year";
  const range =
    period === "custom"
      ? { start: search.start ?? null, end: search.end ?? null }
      : periodRange(period, today);
  return {
    report,
    by,
    business: report === "household" ? null : (search.business ?? null),
    period,
    ...range,
  };
}

/** The filters back as a search string, leaving out what `resolve` would fill in anyway. */
export function toSearch(filters: Filters): ReportSearch {
  const search: ReportSearch = { report: filters.report, period: filters.period };
  const groupings: readonly string[] = GROUPINGS[filters.report];
  if (filters.by && filters.by !== groupings[0]) search.by = filters.by;
  if (filters.business && filters.report !== "household") search.business = filters.business;
  if (filters.period === "custom") {
    if (filters.start) search.start = filters.start;
    if (filters.end) search.end = filters.end;
  }
  return search;
}

/** The query string the report's route takes. */
export function toQuery(filters: Filters): Record<string, string> {
  const query: Record<string, string> = {};
  if (filters.by) query.group_by = filters.by;
  if (filters.business) query.business_id = filters.business;
  if (filters.start) query.start = filters.start;
  if (filters.end) query.end = filters.end;
  return query;
}
